import { castArray, chain, compact, max, min } from 'lodash';
import { DateTime } from 'luxon';
import { OrderHelper } from '../../../dashboard/orders/common/helpers/order.helper';
import type { OrderPoint } from '../../../dashboard/orders/common/interfaces/order-point';
import { DateTimeHelper } from '../../helpers/date-time.helper';
import { DeadlineHelper } from '../../helpers/deadline.helper';
import { RoutePointValueHelper } from '../../helpers/route-point-value.helper';
import { RouteHelper } from '../../helpers/route.helper';
import type { Point } from '../../interfaces/common/point';
import type { TelematicsOrder } from '../../interfaces/telematics/telematics-order';
import type { TelematicsRoutePoint } from '../../interfaces/telematics/telematics-route-point';
import { TelematicsSwapFactory } from './telematics-swap.factory';

type TelematicsFactoryOrder = Omit<
  TelematicsOrder,
  | 'start'
  | 'plannedEnd'
  | 'end'
  | 'canceledAt'
  | 'canceledBeforeStart'
  | 'handoverPoint'
  | 'key'
>;

export class TelematicsOrderFactory {
  public static from(data: {
    routePoints: TelematicsRoutePoint[];
    fleetUuid: string;
    now: DateTime;
  }): TelematicsOrder[] {
    const orders: TelematicsFactoryOrder[] = [];
    let swapPoint: Point | null = null;

    for (const routePoint of data.routePoints) {
      const point = this.mapPoint(routePoint);

      if (RouteHelper.getSwapData(routePoint)) {
        swapPoint = point;
        continue;
      }

      if (!routePoint.order || !routePoint.point) continue;

      const lastOrder = orders.at(-1);
      const existingOrder = castArray(
        routePoint.order.isDedicated ? lastOrder : orders.toReversed(),
      ).find((order) => {
        if (!order) return false;

        const lastIndex = this.getIndexes(order.points).at(-1);
        return (
          order.order?.uuid === routePoint.order.uuid &&
          lastIndex != null &&
          routePoint.point.index === lastIndex + 1
        );
      });

      if (existingOrder) {
        existingOrder.points.push(point);
        continue;
      }

      if (
        swapPoint &&
        lastOrder?.order?.isDedicated &&
        this.hasMissingNextPoints(lastOrder)
      ) {
        lastOrder.points.push(swapPoint);
      }

      orders.push({
        uuid: routePoint.order.uuid,
        fleetUuid: data.fleetUuid,
        order: routePoint.order,
        points: compact([routePoint.point.index > 0 ? swapPoint : null, point]),
      });
    }

    if (swapPoint) {
      for (const order of orders) {
        if (
          (!order.order?.isDedicated || order === orders.at(-1)) &&
          this.hasMissingNextPoints(order)
        ) {
          order.points.push(swapPoint);
        }
      }
    }

    return orders.map((order) => {
      const { points } = order;
      const status = order.order?.status.value ?? null;
      const firstPoint = points[0] ?? null;
      const lastPoint = points.at(-1) ?? null;

      const plannedStart = firstPoint
        ? (DeadlineHelper.getPointDeadline(firstPoint)?.arrival ?? null)
        : null;
      const engineEstimatedStart = DateTimeHelper.fromISO(
        RoutePointValueHelper.getEstimations(firstPoint)?.arrivalTime,
      );
      const finishedStart = DateTimeHelper.fromISO(
        firstPoint?.timestamps.arrivedAt,
      );

      const plannedEnd = RouteHelper.isSwapPoint(lastPoint)
        ? (DeadlineHelper.getPointDeadline(lastPoint)?.departure ??
          TelematicsSwapFactory.getFallbackTimes(lastPoint, data.now).end)
        : (points
            .toReversed()
            .map((point) => DeadlineHelper.getPointDeadline(point)?.departure)
            .find(Boolean) ?? null);
      const engineEstimatedEnd = DateTimeHelper.fromISO(
        RoutePointValueHelper.getEstimations(lastPoint)?.departureTime,
      );
      const finishedEnd = DateTimeHelper.fromISO(
        lastPoint?.timestamps.completedAt,
      );

      const handoverPoint =
        RouteHelper.isSwapPoint(lastPoint) && RouteHelper.isCompleted(lastPoint)
          ? lastPoint
          : null;
      const isCanceled = OrderHelper.isCanceled(status) && !handoverPoint;
      const canceledBeforeStart = isCanceled && !finishedStart;
      const scheduledStart = isCanceled
        ? (plannedStart ?? engineEstimatedStart)
        : (engineEstimatedStart ?? plannedStart);
      const start =
        finishedStart ??
        scheduledStart ??
        (RouteHelper.isSwapPoint(firstPoint)
          ? TelematicsSwapFactory.getFallbackTimes(firstPoint, data.now).start
          : data.now);
      const canceledAt = isCanceled ? this.getCanceledAt(points) : null;
      const canceledEnd = canceledBeforeStart ? null : canceledAt;
      const estimatedEnd = engineEstimatedEnd ?? plannedEnd;
      const end = isCanceled
        ? (canceledEnd ??
          finishedEnd ??
          plannedEnd ??
          engineEstimatedEnd ??
          start)
        : (finishedEnd ??
          (estimatedEnd ? DateTime.max(estimatedEnd, data.now) : data.now));

      return {
        ...order,
        key: this.getKey(order.uuid, points),
        start: isCanceled ? DateTime.min(start, end) : start,
        plannedEnd,
        end,
        canceledAt,
        canceledBeforeStart,
        handoverPoint,
      };
    });
  }

  private static mapPoint(routePoint: TelematicsRoutePoint): Point {
    const point = RouteHelper.getPointValue(routePoint);
    return {
      ...point,
      route: {
        ...point.route,
        ...routePoint.route,
      },
    };
  }

  private static getCanceledAt(points: Point[]): DateTime | null {
    return DateTimeHelper.fromISO(
      points.findLast((point) => point.timestamps.canceledAt)?.timestamps
        .canceledAt,
    );
  }

  private static getIndexes(points: Point[]): number[] {
    return chain(points)
      .filter(
        (point): point is OrderPoint => !RouteHelper.isTechnicalPoint(point),
      )
      .map((point) => point.index)
      .value();
  }

  private static getKey(uuid: string, points: Point[]): string {
    const indexes = this.getIndexes(points);
    return `${uuid}:${min(indexes) ?? 0}-${max(indexes) ?? 0}`;
  }

  private static hasMissingNextPoints(
    order?: TelematicsFactoryOrder,
  ): order is TelematicsFactoryOrder {
    if (!order || RouteHelper.isSwapPoint(order.points.at(-1) ?? null)) {
      return false;
    }

    const indexes = this.getIndexes(order.points);
    const pointsCount = order.order?.pointsCount;
    return !!pointsCount && !!indexes.length && max(indexes)! < pointsCount - 1;
  }
}
